import {Component, OnInit, ChangeDetectionStrategy} from "@angular/core";
import {
    BulkRequestDto,
    KeywordType,
    PerFormaAPIService,
    SaveSearchRequestDto,
    SchemaDto,
    SchemaKeywordDto,
    SearchDocumentDto,
    SearchRequestDto,
    SearchRequestKeywordDto,
    SearchResponseDto,
    SearchSortType,
    SearchType
} from "../services/pfapi.service";
import {KeywordTypes, SearchTypes, SearchSortTypes} from "../models/performa-enums";
import {lastValueFrom} from "rxjs";
import {NzModalService} from "ng-zorro-antd/modal";
import {InputBoxService} from "../services/input-box.service";
import {NzMessageService} from "ng-zorro-antd/message";
import Utils from "src/utils";
import {EmailModalComponent} from "../components/email-modal.component";
import {FileDownloadService} from "../services/file-download.service";
import {EmailData} from "src/models/email-data";
import {endOfDay, format, formatISO, parse, parseISO, startOfDay} from "date-fns";
import {ActivatedRoute} from "@angular/router";

type SearchDocumentDtoExt = SearchDocumentDto & {
    Checked?: boolean;
};
type SearchResponseDtoExt = SearchResponseDto & {
    Documents: SearchDocumentDtoExt[];
};
type SearchRequestKeywordDtoExt = SearchRequestKeywordDto & {
    Type: KeywordType;
    DateValue?: Date;
};
@Component({
    selector: "app-search",
    templateUrl: "./search.component.html",
    styleUrls: ["./search.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SearchComponent implements OnInit {
    constructor(
        private api: PerFormaAPIService,
        private modal: NzModalService,
        private inputBox: InputBoxService,
        private message: NzMessageService,
        private route: ActivatedRoute,
        private fileDownload: FileDownloadService
    ) {}

    // id + mode of the file being fetched right now, so the clicked result link can show a spinner
    downloadingFileId: number | null = null;
    downloadingInline = false;

    // opens a document file through the authed client (a plain <a href> would 401 against the bearer-only API)
    openDocFile = async (id: number | undefined | null, inline: boolean) => {
        if (id == null) return;
        // ignore repeat clicks on the same link while its fetch is still in flight
        if (this.downloadingFileId === id && this.downloadingInline === inline) return;
        this.downloadingFileId = id;
        this.downloadingInline = inline;
        try {
            await this.fileDownload.openDocumentFile(id, inline);
        } finally {
            this.downloadingFileId = null;
        }
    };

    schema: SchemaDto = {Types: [], Keywords: []};
    results: SearchResponseDtoExt = {Documents: []};
    searched = false;
    exporting = false;
    processing = false;
    processingAll = false;
    loading = true;
    documentTypes: number[] = [];
    commonFields: SchemaKeywordDto[] = [];
    searches: SaveSearchRequestDto[] = [];
    keywords: SearchRequestKeywordDtoExt[] = [
        {Operator: 0, Id: null, Value: null, Type: KeywordTypes.Alphanumeric},
        {Operator: 0, Id: null, Value: null, Type: KeywordTypes.Alphanumeric},
        {Operator: 0, Id: null, Value: null, Type: KeywordTypes.Alphanumeric}
    ];
    pageSize = 10;
    page = 0;
    hasMorePages = false;
    searchedPageSize?: number;
    selectedSearch?: SaveSearchRequestDto;
    searchedPage?: number;
    sort?: SearchSortType = SearchSortTypes.NewestToOldest;
    fullText = "";
    previousQuery = "";
    previousFriendlyQuery = "";
    searchAction = "email";
    allChecked = false;
    archiveDate?: Date = null;
    archiveDateOperator: SearchType = SearchTypes.Equals;
    checkedTotal = 0;

    async ngOnInit() {
        try {
            this.loading = true;
            const [schema, searches] = await Promise.all([
                lastValueFrom(this.api.getApiSystemSchema()),
                lastValueFrom(this.api.getApiSearchSearches())
            ]);
            this.schema = schema;
            this.searches = searches;
            this.commonFields = this.schema.Keywords;

            // check if they are getting here using a "back to search" button/link
            let previous = false;

            this.route.queryParams.subscribe(p => {
                if (p.previous === "true") {
                    previous = true;
                }
            });

            if (previous) await this.handleSavedSearch();
        } finally {
            this.loading = false;
        }
    }

    handleSavedSearch = async () => {
        const dto = this.loadParams();

        if (dto) {
            try {
                this.loading = true;
                const resp = await lastValueFrom(this.api.postApiSearchSearch(dto));
                this.searchedPageSize = this.pageSize;
                this.searchedPage = this.page;
                this.results = resp as SearchResponseDtoExt;
                this.hasMorePages = this.results.Total > (this.page + 1) * this.pageSize;
                this.searched = true;
                this.updateCommonFields();
            } catch (e) {
                const json = JSON.parse(await (e.error as Blob).text());
                console.log(`problem with saved search: ${json?.Message}`);
            }
        }
    };

    /**
     * Prompts and deletes the current selection or result set.
     * @param selected Whether to delete just the selected documents or entire result set.
     */
    delete = async (selected?: boolean) => {
        const dto = this.getSelectionDto(selected);
        const count = dto.Selected ? dto.Selection?.length : dto.Total;
        this.modal.confirm({
            nzTitle: `Are you sure you want to delete ${count} documents?`,
            nzOkDanger: true,
            nzOkText: "Delete",
            nzOnOk: async () => {
                this.loading = true;
                try {
                    await lastValueFrom(this.api.deleteApiSearchDelete(dto));
                    await this.search();
                    this.message.success(`Deleted ${count} documents.`);
                } catch (e) {
                    const json = JSON.parse(await (e.error as Blob).text());
                    this.message.error(json?.Message);
                } finally {
                    this.loading = false;
                }
            }
        });
    };

    /**
     * Prompts and deletes the current selection or result set.
     * @param selected Whether to delete just the selected documents or entire result set.
     */
    email = async (selected?: boolean) => {
        this.modal
            .create({
                nzTitle: "Email Documents",
                nzContent: EmailModalComponent,
                nzFooter: null,
                nzWidth: 600,

                nzBodyStyle: {padding: "24px"}
            })
            .afterClose.subscribe(async (res: EmailData) => {
                if (!res) return;
                const dto = this.getSelectionDto(selected);
                const count = dto.Selected ? dto.Selection?.length : dto.Total;
                this.loading = true;
                try {
                    dto.EmailBody = res.message;
                    dto.EmailSubject = res.subject;
                    dto.EmailTo = res.to;
                    await lastValueFrom(this.api.postApiSearchEmail(dto));
                    await this.search();
                    this.message.success(`Emailed ${count} documents.`);
                } catch (e) {
                    const json = JSON.parse(await (e.error as Blob).text());
                    this.message.error(json?.Message);
                } finally {
                    this.loading = false;
                }
            });
    };

    /**
     * Zips and downloads the current selection or result set.
     * @param selected Whether to zip just the selected documents or entire result set.
     */
    zip = async (selected?: boolean) => {
        const dto = this.getSelectionDto(selected);
        try {
            const blob = await lastValueFrom(
                this.api.postApiSearchZip(dto, {
                    observe: "response"
                })
            );
            Utils.downloadFileContent(blob, true);
        } catch (e) {
            const json = JSON.parse(await (e.error as Blob).text());
            this.message.error(json?.Message);
        }
    };

    /**
     * Takes the documents in the current selection or result set, combines them
     * into a single pdf that is then returned to the browser for printing/saving.
     * @param selected Whether to print just the selected documents or entire result set.
     */
    print = async (selected?: boolean) => {
        const dto = this.getSelectionDto(selected);
        try {
            const resp = await lastValueFrom(
                this.api.postApiSearchPrint(dto, {
                    observe: "response"
                })
            );

            // now we get the file data
            const blob = resp.body;

            if (!blob) {
                throw new Error("No file data received");
            }

            // Create a URL for the blob (browser will treat it as inline PDF)
            const fileURL = URL.createObjectURL(blob);

            // Open in a new tab (most browsers will display the PDF inline)
            window.open(fileURL, "_blank");

            // revoke the object URL after a delay to free memory
            // (safe delay since the new tab has already loaded it)
            setTimeout(() => URL.revokeObjectURL(fileURL), 10000);
        } catch (e) {
            const json = JSON.parse(await (e.error as Blob).text());
            this.message.error(json?.Message);
        }
    };

    /**
     * Exports current result set as a CSV.
     */
    export = async () => {
        try {
            let dto;

            this.exporting = true;

            if (this.checkedTotal) {
                dto = this.getSelectionDto(true);
            } else {
                dto = this.getSearchDto() as BulkRequestDto;
                dto.Total = this.results.Total;
            }

            const blob = await lastValueFrom(
                this.api.postApiSearchExport(dto, {
                    observe: "response"
                })
            );
            Utils.downloadFileContent(blob, true);
        } catch (e: any) {
            const json = JSON.parse(await (e.error as Blob).text());
            this.message.error(json?.Message);
        } finally {
            this.exporting = false;
        }
    };

    /**
     * Handles page size event
     */
    onPageSizeChange = async () => {
        this.page = 0;
        await this.search();
        this.allChecked = false;
        this.allCheckedChange();
    };

    /**
     * Handles sort change event.
     */
    onSortChange = async () => {
        this.page = 0;
        await this.search();
    };

    /**
     * Handles keyword dropdown change event.
     */
    onKeywordChange = async (item: SearchRequestKeywordDtoExt) => {
        const match = this.schema.Keywords.find(x => x.Id === item.Id);
        // clear the value when they change between date and text/numeric
        if (
            (item.Type === 3 && match?.KeywordType !== 3) ||
            (match?.KeywordType == 3 && item.Type !== 3) ||
            item.Id === null
        ) {
            item.DateValue = null;
            item.Value = "";
        }
        item.Type = match?.KeywordType || KeywordTypes.Alphanumeric;
        // set the operator to equals when operator set to contains and the field isn't alphanumeric
        if (item?.Operator === 1 && item?.Type !== KeywordTypes.Alphanumeric) {
            item.Operator = 0;
        }
    };
    /**
     * Handles date picker change event.
     */
    onDateChange = async (item: SearchRequestKeywordDtoExt) => {
        if (item.DateValue) {
            item.Value = format(item.DateValue, "MM/dd/yyyy");
        } else {
            item.Value = "";
        }
    };

    /**
     * Toggles all items on the current page selected/unselected.
     */
    allCheckedChange = () => {
        this.results.Documents.forEach((d: SearchDocumentDtoExt) => {
            d.Checked = this.allChecked;
        });

        if (this.allChecked) {
            this.setCheckedCount();
        } else {
            this.checkedTotal = 0;
        }
    };

    /**
     * Performs actions on just selected documents in the current search result set page.
     */
    processSelected = async () => {
        if (!this.checkedTotal) {
            this.message.warning("Select one or more documents to perform this action");
            return;
        }

        try {
            this.processing = true;
            switch (this.searchAction) {
                case "zip":
                    await this.zip(true);
                    break;
                case "email":
                    await this.email(true);
                    break;
                case "print":
                    await this.print(true);
                    break;
                case "delete":
                    await this.delete(true);
                    break;
            }
        } finally {
            this.processing = false;
        }
    };

    /**
     * Performs actions on all documents in the search result set.
     */
    processSelectedAll = async () => {
        try {
            this.processingAll = true;
            switch (this.searchAction) {
                case "zip":
                    await this.zip();
                    break;
                case "email":
                    await this.email();
                    break;
                case "print":
                    await this.print();
                    break;
                case "delete":
                    await this.delete();
                    break;
            }
        } finally {
            this.processingAll = false;
        }
    };

    /**
     * Navigates to the previous page in the result set.
     */
    previousPage = async () => {
        this.page--;
        await this.search();
    };

    /**
     * Navigates to the next page in the result set.
     */
    nextPage = async () => {
        this.page++;
        await this.search();
    };

    /**
     * Searches with the current UI search criteria
     * @param results Whether or not the `Filter Results` button is clicked, indicating a drill down should be done within the current query.
     */
    search = async (results?: boolean) => {
        try {
            this.loading = true;
            const dto = this.getSearchDto(results);
            const resp = await lastValueFrom(this.api.postApiSearchSearch(dto));

            // save the current search params
            this.saveParams(dto);

            this.searchedPageSize = this.pageSize;
            this.searchedPage = this.page;
            this.results = resp as SearchResponseDtoExt;
            this.hasMorePages = this.results.Total > (this.page + 1) * this.pageSize;
            this.searched = true;
        } finally {
            this.loading = false;
        }
    };

    /**
     * Prompts and saves current search criteria
     */
    saveSearch = async () => {
        const name = await this.inputBox.open({title: "Save Search"});
        if (!name) return;
        try {
            this.loading = true;
            const dto = {...this.getSearchDto(), Name: name} as SaveSearchRequestDto;
            // cut off metadata used for UI propagation (Type)
            dto.Keywords = dto.Keywords.map(m => ({Id: m.Id, Operator: m.Operator, Value: m.Value}));
            await lastValueFrom(this.api.postApiSearchSearches(dto));
            this.searches = await lastValueFrom(this.api.getApiSearchSearches());
            this.message.success("Search saved successfully.");
        } finally {
            this.loading = false;
        }
    };

    /**
     * Loads and searches with the currently selected saved search.
     */
    loadSearch = async () => {
        if (!this.selectedSearch) return;
        this.documentTypes = this.selectedSearch.DocumentTypes || [];
        this.fullText = this.selectedSearch.FullText;
        this.previousQuery = this.selectedSearch.PreviousQuery;
        this.previousFriendlyQuery = this.selectedSearch.PreviousFriendlyQuery;
        const keywords: SearchRequestKeywordDtoExt[] =
            Array.isArray(this.selectedSearch.Keywords) && this.selectedSearch.Keywords.length > 0
                ? (this.selectedSearch.Keywords as SearchRequestKeywordDtoExt[])
                : [
                      {Operator: 0, Id: null, Type: KeywordTypes.Alphanumeric, Value: null},
                      {Operator: 0, Id: null, Type: KeywordTypes.Alphanumeric, Value: null},
                      {Operator: 0, Id: null, Type: KeywordTypes.Alphanumeric, Value: null}
                  ];
        const mapped = keywords
            .map(k => ({Keyword: k, Schema: this.schema.Keywords.find(s => s.Id === k.Id)}))
            .filter(x => x.Schema)
            .map(m => ({
                ...m.Keyword,
                Type: m.Schema.KeywordType,
                ...(m.Keyword.Value && m.Schema.KeywordType === KeywordTypes.Date
                    ? {DateValue: parse(m.Keyword.Value, "MM/dd/yyyy", new Date())}
                    : {})
            }));
        if (mapped.length < 3) {
            for (let i = mapped.length; i < 3; i++)
                mapped.push({Operator: 0, Id: null, Type: KeywordTypes.Alphanumeric, Value: null});
        }
        this.keywords = mapped;
        this.archiveDate = this.selectedSearch?.ArchiveDate ? parseISO(this.selectedSearch?.ArchiveDate) : null;
        this.archiveDateOperator = this.archiveDate ? this.selectedSearch.ArchiveDateOperator : SearchTypes.Equals;
        this.page = this.selectedSearch.Pagination?.Page || 0;
        this.pageSize = this.selectedSearch.Pagination?.Size || 10;
        await this.search();
    };

    /**
     * Prompts and deletes currently selected saved search.
     */
    deleteSearch = () => {
        if (!this.selectedSearch) return;
        const search = {...this.selectedSearch};
        this.modal.confirm({
            nzTitle: `Do you want to delete the search "${search?.Name}"?`,
            nzOkDanger: true,
            nzOkText: "Delete",
            nzOnOk: async () => {
                await lastValueFrom(this.api.deleteApiSearchSearchesSearchId(search.Id));
                this.selectedSearch = undefined;
                this.searches = this.searches.filter(x => x.Id !== search.Id);
            }
        });
    };

    /**
     * Builds search criteria DTO based on current search criteria.
     * @param results Whether or not the `Filter Results` button was selected and a subquery should be injected.
     * @returns @see SearchRequestDto based on current search criteria which can be passed into the search API.
     */
    getSearchDto = (results?: boolean) => {
        const dto: SearchRequestDto = {
            FullText: this.fullText,
            Pagination: {Page: this.page, Size: this.pageSize, Sort: this.sort},
            Keywords: this.keywords,
            ArchiveDate: null,
            ArchiveDateOperator: this.archiveDateOperator,
            DocumentTypes: this.documentTypes,
            ...(results ? {PreviousFriendlyQuery: this.previousFriendlyQuery, PreviousQuery: this.previousQuery} : {})
        };

        /**
         * If they are doing a search against Archive Date, we have to adjust the date
         * passed to the server based on the operator being used, adjusting it to either
         * the start of the given day or the end of it. This will ensure a correct search
         * when it determines the ranges to specify.
         *
         * For > and <= we use the end of the given day (11:59:59 PM)
         * For =, <, and >= we use the start of the given day (12:00:00 AM)
         */
        if (this.archiveDate) {
            switch (this.archiveDateOperator) {
                case SearchTypes.GreaterThan:
                case SearchTypes.LessThanOrEqualTo:
                    dto.ArchiveDate = formatISO(endOfDay(this.archiveDate));
                    break;
                case SearchTypes.Equals:
                case SearchTypes.LessThan:
                case SearchTypes.GreaterThanOrEqualTo:
                    dto.ArchiveDate = formatISO(startOfDay(this.archiveDate));
                    break;
            }
        }

        return dto;
    };

    /**
     * Builds bulk request DTO for zip/delete/email/print operations based on current search and selection criteria.
     * @param selected Whether or not all selected button was pressed.
     * @returns @see BulkRequestDto that can be passed into the zip/delete/email API.
     */
    getSelectionDto = (selected?: boolean): BulkRequestDto => {
        const dto = this.getSearchDto() as BulkRequestDto;
        dto.Total = this.results.Total;
        if (selected) {
            dto.Selected = true;
            dto.Selection = this.results.Documents.filter((x: SearchDocumentDtoExt) => x.Checked).map(x => x.Id);
        }
        return dto;
    };

    setCheckedCount = () => {
        this.checkedTotal = this.results.Documents?.filter((x: SearchDocumentDtoExt) => x.Checked).length ?? 0;
    };

    /**
     * Updates underlying common fields based on document type selection.
     */
    updateCommonFields = () => {
        this.commonFields = this.getCommonKeywords();
    };

    /**
     * Retrieves common keywords based on dcoument type selection.
     * @returns Array of common keywords.
     */
    getCommonKeywords(): SchemaKeywordDto[] {
        const keywords = [...this.schema.Keywords];
        const types = this.schema.Types.filter(x => this.documentTypes.includes(x.Id));
        return keywords.filter(k => types.every(t => t.Keywords.some(dt => dt.Id === k.Id)));
    }

    /**
     * Retrieves the content of a document's notes for use in a tooltip
     * @return html table string of document notes
     */
    getDocumentNotes = (document: SearchDocumentDto) => {
        let notes = "";

        if (document.Notes?.length > 0) {
            notes =
                '<table class="table table-striped" style="margin-bottom:0;"><thead><tr><th>Time</th><th>Author</th><th>Note</th></tr></thead>';

            (document.Notes as any[])
                .sort((a, b) => new Date(b.Timestamp).getTime() - new Date(a.Timestamp).getTime())
                .forEach(n => {
                    let noteText = n.Content;
                    noteText = noteText.length > 30 ? noteText.substring(0, 27) + "..." : noteText;
                    notes += `<tr><td>${Utils.FormatDate(new Date(n.Timestamp + "Z"))}</td><td>${n.User}</td><td>${noteText}</td></tr>`;
                });

            notes += "</table>";
        }

        return notes;
    };

    loadParams = () => {
        let dto: SearchRequestDto = null;

        const dtoString = sessionStorage.getItem("lastSearch");

        if (dtoString) dto = JSON.parse(dtoString);

        //try to load form values
        if (dto) {
            this.fullText = dto.FullText;

            if (dto.Pagination) {
                this.page = dto.Pagination.Page;
                this.pageSize = dto.Pagination.Size;
                this.sort = dto.Pagination.Sort;
            }

            this.keywords = dto.Keywords as SearchRequestKeywordDtoExt[];
            this.archiveDate = dto.ArchiveDate ? new Date(dto.ArchiveDate) : null;
            this.archiveDateOperator = dto.ArchiveDateOperator;
            this.documentTypes = dto.DocumentTypes;
            this.previousFriendlyQuery = dto.PreviousFriendlyQuery ?? null;
            this.previousQuery = dto.PreviousQuery ?? null;
        }

        return dto;
    };

    saveParams = (dto: SearchRequestDto) => {
        sessionStorage.setItem("lastSearch", JSON.stringify(dto));
    };
}
